import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, UserPlus, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ASSETS } from "../lib/constants";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Auth({ mode = "login" }) {
  const isSignup = mode === "signup";
  const { login, loginWithPassword, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isSignup) await register(name, email, password);
      else await loginWithPassword(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen w-full relative overflow-hidden flex items-center justify-center px-6 py-16" data-testid="auth-page">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 60% at 50% 0%, rgba(var(--glow),0.16), transparent 65%)" }} />

      <Link
        to="/"
        data-testid="auth-back-home"
        className="absolute top-6 left-6 md:top-8 md:left-10 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/60 hover:text-fg transition-colors"
      >
        <ArrowLeft size={14} /> Back home
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md glass shape-r border border-white/10 p-8 md:p-10"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <img src={ASSETS.logo} alt="Casey Muze" className="h-12 w-auto mb-5" />
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary-c mb-2">
            {isSignup ? "Create your account" : "Welcome back"}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
            {isSignup ? "Begin your rhythm." : "Sign in to continue."}
          </h1>
        </div>

        {error && (
          <p
            data-testid="auth-error"
            className="mb-5 shape-r border border-[#c46a58]/50 bg-[#c46a58]/10 text-[#e79d90] text-sm px-4 py-3"
          >
            {error}
          </p>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {isSignup && (
            <div>
              <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="auth-name-input"
                placeholder="Your name"
                className="w-full shape-r bg-black/30 border border-white/15 focus:border-primary-c focus:outline-none focus:ring-2 focus:ring-primary-c/40 p-3.5 text-fg placeholder:text-fg/30 transition-colors"
              />
            </div>
          )}
          <div>
            <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="auth-email-input"
              placeholder="you@example.com"
              className="w-full shape-r bg-black/30 border border-white/15 focus:border-primary-c focus:outline-none focus:ring-2 focus:ring-primary-c/40 p-3.5 text-fg placeholder:text-fg/30 transition-colors"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={isSignup ? 8 : undefined}
              data-testid="auth-password-input"
              placeholder={isSignup ? "At least 8 characters" : "Your password"}
              className="w-full shape-r bg-black/30 border border-white/15 focus:border-primary-c focus:outline-none focus:ring-2 focus:ring-primary-c/40 p-3.5 text-fg placeholder:text-fg/30 transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 shape-r bg-primary-c text-bg font-medium px-6 py-3.5 hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : isSignup ? <UserPlus size={16} /> : <LogIn size={16} />}
            {busy ? "Please wait…" : isSignup ? "Create account" : "Sign in"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/40">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <button
          onClick={login}
          data-testid="auth-google-btn"
          className="w-full inline-flex items-center justify-center gap-3 shape-r border border-white/20 px-6 py-3.5 font-medium text-fg hover:bg-white/10 transition-colors"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continue with Google
        </button>

        <p className="mt-8 text-center text-sm text-fg/60">
          {isSignup ? (
            <>
              Already have an account?{" "}
              <Link to="/login" data-testid="auth-switch-login" className="text-primary-c hover:underline">Sign in</Link>
            </>
          ) : (
            <>
              New here?{" "}
              <Link to="/signup" data-testid="auth-switch-signup" className="text-primary-c hover:underline">Create an account</Link>
            </>
          )}
        </p>
      </motion.div>
    </div>
  );
}
